import axios from 'axios';
import { cacheUtils } from '../..';
import { CacheTypes } from '../../utilities/cache/interfaces';

interface EmailValidationResponse {
    status: number;
    info: string;
    details: string;
    freemail: boolean;
    ratelimit_remain: number;
    ratelimit_seconds: number;
}

interface EmailValidationRequest {
    emailAddress: string;
}

export class EmailValidator {
    private static readonly API_URL = 'https://api.email-validator.net/api/verify';

    public static async validateEmail(request: EmailValidationRequest): Promise<EmailValidationResponse> {
        try {
            const APIKey = cacheUtils.getCache<CacheTypes.GlobalSettings>("GlobalSettings").find((c) => c.setting === `EmailValidation.APIKey`)?.settingValue;
            if (!APIKey) {
                return {
                    status: 999,
                    info: "EmailValidation.APIKey not set",
                    details: "",
                    freemail: false,
                    ratelimit_remain: 0,
                    ratelimit_seconds: 0,
                };
            }
            const params = new URLSearchParams({
                EmailAddress: request.emailAddress,
                APIKey
            });

            // Use either axios.get or axios.post based on preference
            const response = await axios.get<EmailValidationResponse>(`${EmailValidator.API_URL}?${params.toString()}`);

            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                // Handle API errors (status codes 401, 118, 119)
                const apiError = error.response.data as EmailValidationResponse;
                throw new Error(apiError.info);
            } else {
                // Handle other errors
                throw new Error('An error occurred while validating the email');
            }
        }
    }

    public static handleResponse(response: EmailValidationResponse): void {
        switch (response.status) {
            case 114:
                console.log('Processing: The email address is still being checked. Please wait.');
                break;
            case 118:
                console.error('Limit Exceeded: Too many requests. Try again later.');
                break;
            case 119:
                console.error('Key Problem: Your API key is invalid or finished. Report to manager to top up balance.');
                break;
            case 121:
                console.log('Accepted: The email validation request has been accepted and is being processed.');
                break;
            case 200:
                console.log('Valid: The email address is valid.');
                break;
            case 207:
                console.log('Valid But Generic: The email address is valid, but it is a catch-all address.');
                break;
            case 215:
                console.log('Check Delayed: There is a temporary delay in validating this email. Please wait.');
                break;
            case 302:
                console.error('Invalid: The email address is incomplete and missing a domain.');
                break;
            case 303:
                console.error('Invalid: The email address is using an IP address instead of a domain.');
                break;
            case 305:
                console.error('Temporary: The email address is from a disposable email service.');
                break;
            case 308:
                console.error('Generic: The email address is a role-based email and not for a specific person.');
                break;
            case 313:
                console.error('Server Issue: The mail server is not responding. Try again later.');
                break;
            case 314:
                console.error('Unavailable: The email address is not available according to the server.');
                break;
            case 316:
                console.log('Duplicate: This email address has already been checked.');
                break;
            case 317:
                console.error('Server Rejection: The mail server is rejecting requests. Try again later.');
                break;
            case 401:
                console.error('Invalid: The email address is not correctly formatted.');
                break;
            case 404:
                console.error('Invalid: The email address domain is incomplete or not fully qualified.');
                break;
            case 406:
                console.error('Delivery Issue: There is a problem with the email domain’s mail server.');
                break;
            case 409:
                console.error('No-Reply: The email address appears to be a no-reply and is not usable.');
                break;
            case 410:
                console.error('Rejected: The email server rejected the address.');
                break;
            case 413:
                console.error('Server Issue: The mail server is not responding for a long time.');
                break;
            case 414:
                console.error('Unavailable: The mailbox is full or temporarily unavailable.');
                break;
            case 420:
                console.error('Misspelled: The domain name in the email address might be misspelled.');
                break;
            // Add similar cases for any other status codes...

            default:
                console.error(`Unknown Status: Code ${response.status}. Please try again or contact support.`);
        }
    }
}
